import {
  getSpeculativeLoadingBudget,
  mediaQueryLarge,
  prefersReducedMotion,
  requestIdleCallback,
  startViewTransition,
} from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { StandardEvents } from '@shopify/events';
import { getIntersectionRoot, scrollTo } from '@theme/scroll-container';

const COLLECTION_VIEW_STORAGE_KEY = 'tfp-collection-view-v1';
const COLLECTION_VIEWS = new Set(['standard', 'lookbook', 'single']);
const SEARCH_VIEWS = new Set(['standard', 'lookbook']);
const EDITORIAL_VIEW_STORAGE_KEY = 'tfp-editorial-view-v1';
const EDITORIAL_VIEWS = new Set(['editorial', 'quick-buy']);
const ADAPTIVE_DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');
const MAX_ADAPTIVE_APPENDS = 8;
const COLLECTION_MEDIA_COALESCE_MS = 140;

let editorialQuickBuyRuntime;

function loadEditorialQuickBuyRuntime() {
  editorialQuickBuyRuntime ||= import('@theme/product-form').catch((error) => {
    editorialQuickBuyRuntime = undefined;
    console.warn('[results-list] Quick Buy runtime unavailable:', error);
  });
  return editorialQuickBuyRuntime;
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  // Only decoded, already-pending collection media enters these bounded cohorts.
  // Network scheduling and the initial eager/LCP surface remain ProductCard owners.
  #mediaRevealCohorts = new Map();

  queueCardMediaReveal(card) {
    if (!this.isConnected || this.dataset.catalogViewContract !== 'standard-lookbook' ||
        !this.closest('collection-component') || !card.isMediaRevealReady()) return false;
    const grid = this.refs.grid;
    const item = card.closest('.product-grid__item');
    if (!grid || item?.parentElement !== grid) return false;
    const bounds = item.getBoundingClientRect();
    // Finish lookahead work before the shopper reaches it; never animate a
    // previously visible card again just to manufacture a synchronized entrance.
    if (bounds.top >= window.innerHeight || bounds.bottom <= 0 || prefersReducedMotion()) return false;

    const top = item.offsetTop;
    const sameRow = (peer) => peer instanceof HTMLElement &&
      peer.classList.contains('product-grid__item') && Math.abs(peer.offsetTop - top) <= 1;
    let anchor = item;
    while (sameRow(anchor.previousElementSibling)) anchor = anchor.previousElementSibling;
    const row = [];
    for (let peer = anchor; sameRow(peer); peer = peer.nextElementSibling) row.push(peer);
    let cohort = this.#mediaRevealCohorts.get(anchor);
    if (!cohort) {
      cohort = { cards: new Set(), timer: undefined, frame: undefined };
      this.#mediaRevealCohorts.set(anchor, cohort);
      // This deadline starts with the first ready member and is never extended.
      cohort.timer = window.setTimeout(() => this.#flushMediaRevealCohort(anchor), COLLECTION_MEDIA_COALESCE_MS);
    }
    cohort.cards.add(card);
    const pending = row.flatMap((peer) => Array.from(peer.querySelectorAll('product-card'))).filter((peer) =>
      peer.querySelector('[data-media-pending]')
    );
    if (pending.every((peer) => peer.isMediaRevealReady?.())) {
      for (const peer of pending) cohort.cards.add(peer);
      if (cohort.frame === undefined) {
        cohort.frame = requestAnimationFrame(() => this.#flushMediaRevealCohort(anchor));
      }
    }
    return true;
  }

  cancelCardMediaReveal(card) {
    for (const [anchor, cohort] of this.#mediaRevealCohorts) {
      cohort.cards.delete(card);
      if (cohort.cards.size) continue;
      window.clearTimeout(cohort.timer);
      if (cohort.frame !== undefined) cancelAnimationFrame(cohort.frame);
      this.#mediaRevealCohorts.delete(anchor);
    }
  }

  #flushMediaRevealCohort(anchor) {
    const cohort = this.#mediaRevealCohorts.get(anchor);
    if (!cohort) return;
    this.#mediaRevealCohorts.delete(anchor);
    window.clearTimeout(cohort.timer);
    if (cohort.frame !== undefined) cancelAnimationFrame(cohort.frame);
    for (const card of cohort.cards) {
      if (card.isConnected && card.closest('results-list') === this) card.revealReadyMedia();
    }
  }

  #flushAllMediaRevealCohorts() {
    for (const anchor of this.#mediaRevealCohorts.keys()) this.#flushMediaRevealCohort(anchor);
  }

  #successfulAdaptiveAppends = 0;

  #renderedCollectionSignature = '';

  #collectionPopRenderPending = false;

  #collectionPopRenderGeneration = 0;

  #searchPopRenderGeneration = 0;

  #adaptiveHistoryRestoreGeneration = 0;

  #adaptiveHistoryRestorePending = false;

  /** @type {IntersectionObserver | undefined} */
  #backToTopObserver;

  /** @type {IntersectionObserver | undefined} */
  #editorialMediaObserver;

  /** @type {MutationObserver | undefined} */
  #editorialGridObserver;

  /** @type {HTMLElement | undefined} */
  #editorialObservedGrid;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    if (this.#isCollectionCatalog()) {
      this.#renderedCollectionSignature = this.#collectionSignature();
      this.#setCollectionNavigationState('ready');

      if (this.#isSearchResults()) {
        window.addEventListener('popstate', this.#handleSearchPopState);
      } else {
        document.addEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
        window.addEventListener('popstate', this.#handleCollectionPopState);
        window.addEventListener('pageshow', this.#handleCollectionPageShow);
        const state = typeof history.state === 'object' && history.state !== null ? history.state : {};
        if (!state.tfpCollectionHistory) history.replaceState({ ...state, tfpCollectionHistory: true }, '');
      }

      const initialRestore = !this.hasAttribute('initialized');
      requestAnimationFrame(() => {
        if (this.isConnected) this.#restoreCollectionView(initialRestore);
      });
    }
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    this.#flushAllMediaRevealCohorts();
    this.#collectionPopRenderGeneration += 1;
    this.#searchPopRenderGeneration += 1;
    this.#collectionPopRenderPending = false;
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    if (this.#isCollectionCatalog()) {
      if (this.#isSearchResults()) {
        window.removeEventListener('popstate', this.#handleSearchPopState);
      } else {
        document.removeEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
        window.removeEventListener('popstate', this.#handleCollectionPopState);
        window.removeEventListener('pageshow', this.#handleCollectionPageShow);
      }
      this.#cancelBackToTop();
      this.#editorialMediaObserver?.disconnect();
      this.#editorialMediaObserver = undefined;
      this.#editorialGridObserver?.disconnect();
      this.#editorialGridObserver = undefined;
      this.#editorialObservedGrid = undefined;
      this.#adaptiveHistoryRestoreGeneration += 1;
      this.#adaptiveHistoryRestorePending = false;
    }
    super.disconnectedCallback();
  }

  #cancelBackToTop() {
    this.#backToTopObserver?.disconnect();
    this.#backToTopObserver = undefined;

    const { backToTop } = this.refs;
    if (backToTop instanceof HTMLButtonElement) {
      backToTop.removeAttribute('data-visible');
      backToTop.setAttribute('aria-hidden', 'true');
      backToTop.tabIndex = -1;
    }
  }

  #armBackToTop() {
    this.#cancelBackToTop();

    const { grid, backToTop } = this.refs;
    if (!(grid instanceof HTMLElement) || !(backToTop instanceof HTMLButtonElement)) return;
    if (!('IntersectionObserver' in window)) return;

    const cards = Array.from(grid.children).filter(
      (child) => child instanceof HTMLElement && child.classList.contains('product-grid__item')
    );
    if (cards.length < 4) return;

    const resolvedColumns = getComputedStyle(grid).gridTemplateColumns;
    const columnCount = resolvedColumns && resolvedColumns !== 'none'
      ? Math.max(1, resolvedColumns.split(/\s+/).filter(Boolean).length)
      : 1;

    // Observe the first card of the fourth visual row. This keeps the trigger
    // perceptually consistent across Standard, Lookbook and Editorial layouts.
    const triggerCard = cards[columnCount * 3];
    if (!(triggerCard instanceof HTMLElement)) return;

    this.#backToTopObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const rootTop = entry.rootBounds?.top ?? 0;
        const isPastUsefulDepth = !entry.isIntersecting && entry.boundingClientRect.bottom < rootTop;

        backToTop.toggleAttribute('data-visible', isPastUsefulDepth);
        backToTop.setAttribute('aria-hidden', String(!isPastUsefulDepth));
        backToTop.tabIndex = isPastUsefulDepth ? 0 : -1;
      },
      { root: getIntersectionRoot(), threshold: 0 }
    );

    this.#backToTopObserver.observe(triggerCard);
  }

  backToTop() {
    scrollTo({
      top: 0,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * Enhances the real next-page anchor on mobile while preserving it as the no-JavaScript fallback.
   * @param {MouseEvent} event
   */
  async loadMore(event) {
    if (
      (ADAPTIVE_DESKTOP_QUERY.matches && !this.hasAttribute('data-adaptive-pagination-locked')) ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const loadMore = event.target instanceof Element ? event.target.closest('[ref="loadMore"]') : null;
    if (!(loadMore instanceof HTMLAnchorElement)) return;
    if (this.#successfulAdaptiveAppends >= MAX_ADAPTIVE_APPENDS) return;

    event.preventDefault();
    if (this.getAttribute('aria-busy') === 'true') return;

    const appendGeneration = this.#adaptiveHistoryRestoreGeneration;
    const navigationGeneration = this.#collectionPopRenderGeneration;
    const isCurrentAppend = () =>
      this.isConnected &&
      appendGeneration === this.#adaptiveHistoryRestoreGeneration &&
      navigationGeneration === this.#collectionPopRenderGeneration;

    this.#setLoadMoreBusy(true);
    try {
      const result = await this.appendNextPage({ historyMode: 'replace' });
      if (!isCurrentAppend()) return;
      if (!result) throw new Error('No products were appended');
      this.#successfulAdaptiveAppends += 1;
      this.dataset.adaptivePaginationLocked = 'true';
      this.#updateLoadMorePresentation(result.nextUrl, result.addedElements.length);
      this.#restoreCollectionView();
    } catch {
      if (!isCurrentAppend()) return;
      const { loadMoreLabel, loadMoreStatus } = this.refs;
      if (loadMoreLabel) loadMoreLabel.textContent = this.dataset.loadMoreRetryLabel ?? '';
      if (loadMoreStatus) loadMoreStatus.textContent = this.dataset.loadMoreErrorMessage ?? '';
    } finally {
      if (isCurrentAppend()) this.#setLoadMoreBusy(false);
    }
  }

  #setLoadMoreBusy(isBusy) {
    const { grid, loadMore, loadMoreLabel } = this.refs;
    if (isBusy) {
      this.setAttribute('aria-busy', 'true');
      grid?.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
      grid?.removeAttribute('aria-busy');
    }
    if (loadMore) loadMore.setAttribute('aria-disabled', String(isBusy));
    if (isBusy && loadMoreLabel) loadMoreLabel.textContent = this.dataset.loadMoreLoadingLabel ?? '';
  }

  #updateLoadMorePresentation(nextUrl, addedCount) {
    const { grid, loadMoreStatus, loadMoreControl } = this.refs;
    if (!(grid instanceof HTMLElement) || !(loadMoreControl instanceof HTMLElement)) return;

    const previousLoaded = Number(loadMoreControl.dataset.loadedCount) || 0;
    const total = Number(loadMoreControl.dataset.totalCount) || previousLoaded + addedCount;
    const loaded = Math.min(total, previousLoaded + addedCount);

    if (loadMoreStatus) {
      loadMoreStatus.textContent = this.#formatTemplate(this.dataset.loadMoreLoadedTemplate ?? '', {
        count: addedCount,
        loaded,
        total,
      });
    }

    this.#applyLoadMorePresentation({ nextUrl, loaded, total });
  }

  #applyLoadMorePresentation({ nextUrl, loaded, total }) {
    const { loadMore, loadMoreLabel, loadMoreControl, loadMoreComplete } = this.refs;
    if (!(loadMoreControl instanceof HTMLElement)) return;

    loadMoreControl.dataset.loadedCount = String(loaded);
    this.#updateLoadMoreProgress(loaded, total);
    if (loadMoreLabel) loadMoreLabel.textContent = this.dataset.loadMoreIdleLabel ?? '';
    if (loadMore instanceof HTMLAnchorElement) {
      if (nextUrl) {
        loadMore.href = nextUrl;
        loadMore.hidden = false;
        if (loadMoreComplete instanceof HTMLElement) loadMoreComplete.hidden = true;
      } else {
        const returnFocus = document.activeElement === loadMore;
        loadMore.removeAttribute('href');
        loadMore.hidden = true;
        if (loadMoreComplete instanceof HTMLElement) {
          loadMoreComplete.hidden = false;
          if (returnFocus) loadMoreComplete.focus({ preventScroll: true });
        }
      }
    }
  }

  #updateLoadMoreProgress(loaded, total) {
    const { loadMoreProgressText, loadMoreProgressValue, loadMoreControl, loadMore, loadMoreCount } = this.refs;
    const template = loadMoreControl instanceof HTMLElement ? loadMoreControl.dataset.progressTemplate ?? '' : '';
    if (loadMoreProgressText) {
      loadMoreProgressText.textContent = this.#formatTemplate(template, { loaded, total });
    }
    if (loadMoreProgressValue instanceof HTMLElement) {
      const progress = total > 0 ? Math.min(100, Math.max(0, (loaded / total) * 100)) : 100;
      loadMoreProgressValue.style.setProperty('--collection-load-progress', `${progress}%`);
      if (loadMore instanceof HTMLAnchorElement) {
        loadMore.style.setProperty('--collection-load-progress', `${progress}%`);
      }
    }
    if (loadMoreCount instanceof HTMLElement) loadMoreCount.textContent = `(${loaded}/${total})`;
  }

  #formatTemplate(template, values) {
    return Object.entries(values).reduce(
      (copy, [key, value]) => copy.replaceAll(`__${key}__`, String(value)),
      template
    );
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    if (this.#isCollectionCatalog() && !this.#allowedCollectionViews().has(value)) return;

    if (this.#isCollectionCatalog()) {
      // Layout and controls change together; no idle wait, fetch, or temporary hidden cards.
      this.#setLayout(value);
      this.#syncCollectionControls(value);
      this.#armBackToTop();
      if (!Shopify.designMode) {
        try {
          sessionStorage.setItem(this.#isEditorialShopping() ? EDITORIAL_VIEW_STORAGE_KEY : COLLECTION_VIEW_STORAGE_KEY, value);
        } catch {}
      }
      return;
    }

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    this.#flushAllMediaRevealCohorts();
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);

    if (this.#isEditorialShopping()) {
      if (value === 'quick-buy') void loadEditorialQuickBuyRuntime();

      if (value !== 'editorial') {
        this.#editorialMediaObserver?.disconnect();
        this.#editorialMediaObserver = undefined;
        this.#editorialGridObserver?.disconnect();
        this.#editorialGridObserver = undefined;
        this.#editorialObservedGrid = undefined;
      }

      for (const card of grid.querySelectorAll('.tfp-editorial-buy')) {
        const primary = card.querySelector('img[data-editorial-primary]');
        if (!(primary instanceof HTMLImageElement)) continue;
        const isSingle = card.classList.contains('tfp-editorial-buy--single');
        const isTriptych = card.classList.contains('tfp-editorial-buy--triptych');
        primary.sizes = value === 'quick-buy' || (!isSingle && !isTriptych)
          ? '(min-width: 750px) 25vw, 50vw'
          : isTriptych ? '(min-width: 750px) 33vw, 66vw' : '(min-width: 750px) 50vw, 100vw';
      }
      if (value === 'editorial') this.#observeEditorialMedia();
      return;
    }

    if (!this.#usesClassicCollectionViews() || !COLLECTION_VIEWS.has(value)) return;

    for (const card of grid.querySelectorAll('product-card')) {
      const collectionCard = /** @type {HTMLElement & {syncCollectionView?: (view: 'standard'|'lookbook'|'single') => void}} */ (card);
      collectionCard.syncCollectionView?.(value);
    }

    for (const sizes of grid.querySelectorAll('product-card-sizes')) {
      sizes.toggleAttribute('data-auto-open', value === 'single' && !mediaQueryLarge.matches);
      sizes.syncCollectionView?.();
      if (sizes.hasAttribute('data-auto-open') && !customElements.get('product-card-sizes')) {
        sizes.dispatchEvent(new CustomEvent('tfp:card-sizes-auto-open', { bubbles: true }));
      }
    }
  }

  #observeEditorialMedia() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;
    if (!('IntersectionObserver' in window)) {
      for (const template of grid.querySelectorAll('template[data-editorial-extra]')) {
        const media = template.closest('.tfp-editorial-buy__media');
        if (!(template instanceof HTMLTemplateElement) || !(media instanceof HTMLElement)) continue;
        media.append(template.content.cloneNode(true));
        template.remove();
      }
      return;
    }

    const editorialBudget = getSpeculativeLoadingBudget();
    const editorialLookahead = editorialBudget === 'none' ? 0 : editorialBudget === 'conservative' ? 300 : 600;

    this.#editorialMediaObserver ??= new IntersectionObserver((entries) => {
      if (!this.isConnected || this.refs.grid?.getAttribute('product-grid-view') !== 'editorial') return;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        this.#editorialMediaObserver?.unobserve(entry.target);
        const template = entry.target.querySelector('template[data-editorial-extra]');
        const media = entry.target.querySelector('.tfp-editorial-buy__media');
        if (!(template instanceof HTMLTemplateElement) || !(media instanceof HTMLElement)) continue;
        media.append(template.content.cloneNode(true));
        template.remove();
      }
    }, { rootMargin: `${editorialLookahead}px` });

    const observeCards = () => {
      if (grid.getAttribute('product-grid-view') !== 'editorial') return;
      for (const template of grid.querySelectorAll('template[data-editorial-extra]')) {
        const card = template.closest('.tfp-editorial-buy');
        if (card) this.#editorialMediaObserver?.observe(card);
      }
    };
    observeCards();
    if (this.#editorialObservedGrid !== grid) {
      this.#editorialGridObserver?.disconnect();
      this.#editorialGridObserver = new MutationObserver(observeCards);
      this.#editorialGridObserver.observe(grid, { childList: true, subtree: true });
      this.#editorialObservedGrid = grid;
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    if (this.#isCollectionCatalog()) {
      this.#restoreCollectionView();
      return;
    }

    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Whether this results list owns the Collection Standard/Lookbook contract.
   *
   * @returns {boolean}
   */
  #isCollectionCatalog() {
    return this.dataset.catalogViewContract === 'standard-lookbook' ||
      this.dataset.catalogViewContract === 'editorial-shopping';
  }

  #isSearchResults() {
    return this.dataset.pageType === 'search';
  }

  #usesClassicCollectionViews() {
    return this.dataset.catalogViewContract === 'standard-lookbook';
  }

  #isEditorialShopping() {
    return this.dataset.catalogViewContract === 'editorial-shopping';
  }

  #allowedCollectionViews() {
    return this.#isEditorialShopping() ? EDITORIAL_VIEWS : this.#isSearchResults() ? SEARCH_VIEWS : COLLECTION_VIEWS;
  }

  #collectionSignature(url = new URL(window.location.href)) {
    const signature = new URL(url);
    signature.hash = '';
    signature.searchParams.delete('page');
    signature.searchParams.sort();
    return `${signature.pathname}?${signature.searchParams.toString()}`;
  }

  #setCollectionNavigationState(state, generation = this.#collectionPopRenderGeneration) {
    this.dataset.collectionNavigationState = state;
    this.dataset.collectionNavigationGeneration = String(generation);
    const isPending = state === 'pending';
    if (isPending) {
      this.setAttribute('aria-busy', 'true');
      this.refs.grid?.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
      this.refs.grid?.removeAttribute('aria-busy');
    }
  }

  #getCollectionTargetId() {
    try {
      return decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return window.location.hash.slice(1);
    }
  }

  #restoreCollectionPosition(targetId, scrollTop) {
    // scroll-container.js is the single scroll-state owner and captures the exact
    // position on pagehide/pushState. Prefer that precise journey state; the card
    // anchor remains the structural fallback when no saved position is available.
    if (Number.isFinite(scrollTop) && scrollTop >= 0) {
      scrollTo({ top: scrollTop, behavior: 'instant' });
      return;
    }

    const targetCard = targetId ? document.getElementById(targetId) : null;
    targetCard?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }

  #handleCollectionPageShow = (event) => {
    const state = typeof history.state === 'object' && history.state !== null ? history.state : {};

    // A BFCache return already has the exact Collection DOM frozen in memory.
    // Do not rebuild or rescan the adaptive range; only restore the saved
    // journey position and make sure speculative loading watches the live link.
    if (event.persisted) {
      const targetId = this.#getCollectionTargetId();
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#restoreCollectionPosition(targetId, Number(state.scrollTop));
        this.#setCollectionNavigationState('ready');
      });
      return;
    }

    this.#restoreAdaptiveHistory(state).catch(() => {});
  };

  async #restoreAdaptiveHistory(state) {
    if (!this.#isCollectionCatalog() || this.paginationMode !== 'adaptive') return;

    const startPage = Number(state.tfpCollectionStartPage) || 1;
    const endPage = Number(state.tfpCollectionPage);
    const targetId = this.#getCollectionTargetId();
    if (
      !Number.isInteger(startPage) ||
      !Number.isInteger(endPage) ||
      startPage < 1 ||
      endPage <= startPage ||
      endPage - startPage > MAX_ADAPTIVE_APPENDS
    )
      return false;

    const restoreGeneration = ++this.#adaptiveHistoryRestoreGeneration;
    this.#adaptiveHistoryRestorePending = true;
    this.#setCollectionNavigationState('pending', restoreGeneration);

    try {
      const result = await this.restoreAdaptiveRange({
        startPage,
        endPage,
        maxPages: MAX_ADAPTIVE_APPENDS + 1,
      });
      if (restoreGeneration !== this.#adaptiveHistoryRestoreGeneration) return true;
      if (!result) throw new Error('Unable to restore adaptive collection history');

      if (result.endPage !== endPage) {
        const repairedUrl = new URL(window.location.href);
        const currentPage = Number(repairedUrl.searchParams.get('page'));
        if (Number.isInteger(currentPage) && currentPage > result.endPage) {
          repairedUrl.searchParams.set('page', String(result.endPage));
        }
        history.replaceState(
          { ...state, tfpCollectionPage: result.endPage },
          '',
          repairedUrl.toString()
        );
      }

      this.#successfulAdaptiveAppends = result.appendCount;
      if (result.appendCount > 0) this.dataset.adaptivePaginationLocked = 'true';

      const total = Number(this.refs.loadMoreControl?.dataset.totalCount) || result.loadedCount;
      this.#applyLoadMorePresentation({ nextUrl: result.nextUrl, loaded: result.loadedCount, total });
      this.#restoreCollectionView();
      this.#renderedCollectionSignature = this.#collectionSignature();

      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (restoreGeneration !== this.#adaptiveHistoryRestoreGeneration) return true;
      this.#restoreCollectionPosition(targetId, Number(state.scrollTop));
      this.#adaptiveHistoryRestorePending = false;
      this.#setCollectionNavigationState('ready', restoreGeneration);
      return true;
    } catch {
      if (restoreGeneration !== this.#adaptiveHistoryRestoreGeneration) return true;
      this.#adaptiveHistoryRestorePending = false;
      this.#setCollectionNavigationState('error', restoreGeneration);
      this.#restoreCollectionPosition(targetId, Number(state.scrollTop));
      return true;
    }
  }

  /**
   * Restores the collection view after initial render or a section morph.
   * @param {boolean} [initialRestore] - Whether the untouched server-rendered view can be retained.
   */
  #restoreCollectionView(initialRestore = false) {
    if (!this.#isCollectionCatalog()) return;

    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;

    const allowedViews = this.#allowedCollectionViews();
    const defaultView = allowedViews.has(grid.dataset.catalogViewDefault)
      ? grid.dataset.catalogViewDefault
      : this.#isEditorialShopping() ? 'editorial' : 'standard';
    let view = defaultView;

    if (!Shopify.designMode) {
      try {
        const storedView = sessionStorage.getItem(this.#isEditorialShopping() ? EDITORIAL_VIEW_STORAGE_KEY : COLLECTION_VIEW_STORAGE_KEY);
        if (storedView && allowedViews.has(storedView)) view = storedView;
      } catch {
        view = defaultView;
      }
    }

    if (view === 'single' && mediaQueryLarge.matches) view = 'standard';

    if (
      !initialRestore ||
      Shopify.designMode ||
      view !== 'standard' ||
      grid.getAttribute('product-grid-view') !== view
    ) {
      this.#setLayout(view);
    }
    this.#syncCollectionControls(view);
    this.#armBackToTop();
  }

  /**
   * Reapplies the collection view after filter, sort, or clear-all section rendering.
   *
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #handleCollectionUpdate = (event) => {
    if (!this.#isCollectionCatalog()) return;

    const eventSection = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
    const ownSection = this.closest('.shopify-section');
    if (eventSection && ownSection && eventSection !== ownSection) return;

    const navigationGeneration = ++this.#collectionPopRenderGeneration;
    this.#collectionPopRenderPending = false;
    this.#resetAdaptiveState();
    this.#setCollectionNavigationState('pending', navigationGeneration);

    if (!event.promise) {
      this.#setCollectionNavigationState('ready', navigationGeneration);
      return;
    }

    event.promise
      .then(() =>
        requestAnimationFrame(() => {
          if (!this.isConnected || navigationGeneration !== this.#collectionPopRenderGeneration) return;
          this.#renderedCollectionSignature = this.#collectionSignature();
          this.#restoreCollectionView();
          this.#setCollectionNavigationState('ready', navigationGeneration);
        })
      )
      .catch(() => {
        if (this.isConnected && navigationGeneration === this.#collectionPopRenderGeneration) {
          // A superseded append cannot release this state; the current filter failure does.
          this.#setLoadMoreBusy(false);
          if (this.refs.loadMoreLabel) this.refs.loadMoreLabel.textContent = this.dataset.loadMoreIdleLabel ?? '';
          this.#setCollectionNavigationState('error', navigationGeneration);
        }
      });
  };

  #handleSearchPopState = () => {
    if (!this.#isSearchResults()) return;

    const renderGeneration = ++this.#searchPopRenderGeneration;
    this.setAttribute('aria-busy', 'true');
    this.refs.grid?.setAttribute('aria-busy', 'true');

    import('@theme/section-renderer')
      .then(({ sectionRenderer }) => {
        if (!this.isConnected || renderGeneration !== this.#searchPopRenderGeneration) return;
        return sectionRenderer.renderSection(this.sectionId, { cache: false, url: new URL(window.location.href) });
      })
      .then(() => {
        if (!this.isConnected || renderGeneration !== this.#searchPopRenderGeneration) return;
        this.#renderedCollectionSignature = this.#collectionSignature();
        this.#restoreCollectionView();
        this.removeAttribute('aria-busy');
        this.refs.grid?.removeAttribute('aria-busy');
      })
      .catch(() => {
        if (!this.isConnected || renderGeneration !== this.#searchPopRenderGeneration) return;
        this.removeAttribute('aria-busy');
        this.refs.grid?.removeAttribute('aria-busy');
        window.location.reload();
      });
  };

  #handleCollectionPopState = (event) => {
    if (!this.#isCollectionCatalog() || !event.state?.tfpCollectionHistory) return;

    const renderGeneration = ++this.#collectionPopRenderGeneration;
    const scrollTop = Number(event.state.scrollTop);
    const targetId = this.#getCollectionTargetId();

    const existingTargetCard = targetId ? document.getElementById(targetId) : null;
    if (
      !this.#adaptiveHistoryRestorePending &&
      !this.#collectionPopRenderPending &&
      existingTargetCard &&
      this.#renderedCollectionSignature === this.#collectionSignature()
    ) {
      requestAnimationFrame(() => {
        if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration) return;
        this.#restoreCollectionPosition(targetId, scrollTop);
        this.#setCollectionNavigationState('ready', renderGeneration);
      });
      return;
    }

    this.#collectionPopRenderPending = true;
    this.#setCollectionNavigationState('pending', renderGeneration);
    this.#resetAdaptiveState();
    import('@theme/section-renderer')
      .then(({ sectionRenderer }) => {
        if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration) return;
        // Back/Forward targets a URL the shopper already rendered. Reuse the
        // existing SectionRenderer cache when present; cache misses still fetch.
        return sectionRenderer.renderSection(this.sectionId, { cache: true, url: new URL(window.location.href) });
      })
      .then(async () => {
        if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration) return;
        this.#collectionPopRenderPending = false;
        this.#renderedCollectionSignature = this.#collectionSignature();

        const filtersPanel = document.querySelector('#filters-panel');
        if (
          filtersPanel instanceof HTMLElement &&
          filtersPanel.isOpen &&
          typeof filtersPanel.refreshLazyFragment === 'function'
        ) {
          // Popstate renders the collection section directly, bypassing FacetsForm's
          // normal panel refresh. Keep the open panel's option state in step with
          // the URL, without allowing a fragment failure to disrupt history restore.
          await filtersPanel.refreshLazyFragment().catch(() => {});
          if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration) return;
        }

        const restoredRange = await this.#restoreAdaptiveHistory(event.state);
        if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration || restoredRange) return;

        requestAnimationFrame(() => {
          if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration) return;
          this.#restoreCollectionView();
          this.#restoreCollectionPosition(targetId, scrollTop);
          this.#setCollectionNavigationState('ready', renderGeneration);
        });
      })
      .catch(() => {
        if (this.isConnected && renderGeneration === this.#collectionPopRenderGeneration) {
          this.#collectionPopRenderPending = false;
          this.#setCollectionNavigationState('error', renderGeneration);
          window.location.reload();
        }
      });
  };

  #resetAdaptiveState() {
    this.#adaptiveHistoryRestoreGeneration += 1;
    this.#adaptiveHistoryRestorePending = false;
    this.resetPaginationState();
    this.#successfulAdaptiveAppends = 0;
    this.removeAttribute('data-adaptive-pagination-locked');
  }

  /**
   * Keeps both mobile and desktop collection controls in sync.
   *
   * @param {string} value
   */
  #syncCollectionControls(value) {
    for (const option of this.querySelectorAll('input[name="tfp-collection-view"]')) {
      if (option instanceof HTMLInputElement) option.checked = option.value === value;
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
